"use client";

import { useState, useEffect } from "react";
import { CircleAlertIcon, CircleCheckIcon } from "lucide-react";
import { format, addMinutes, isBefore, set } from "date-fns";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export default function TimePicker({
  selectedTime,
  onTimeSelect,
  isValid,
}: {
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  isValid: undefined | boolean;
}) {
  const [timeSlots, setTimeSlots] = useState<Date[]>([]);

  const TIME_FROM_NOW_IN_MINUTES = 15; // Time before first slot
  const INTERVAL_IN_MINUTES = 15;
  const CLOSING_HOUR = 23;

  useEffect(() => {
    // Generate time slots in intervals from current time to closing
    const generateTimeSlots = () => {
      const slots: Date[] = [];
      const now = new Date();

      // Round up to the next interval
      const minutes = now.getMinutes();
      const remainder = minutes % INTERVAL_IN_MINUTES;
      const roundedTime = addMinutes(
        now,
        remainder > 0
          ? INTERVAL_IN_MINUTES + TIME_FROM_NOW_IN_MINUTES - remainder
          : 0
      );

      // Set seconds and milliseconds to 0
      let currentSlot = set(roundedTime, { seconds: 0, milliseconds: 0 });

      // Set midnight time for comparison
      const closingTime = set(new Date(), {
        hours: CLOSING_HOUR,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      });

      // Generate slots until midnight
      while (isBefore(currentSlot, closingTime)) {
        slots.push(currentSlot);
        currentSlot = addMinutes(currentSlot, INTERVAL_IN_MINUTES);
      }

      return slots;
    };

    setTimeSlots(generateTimeSlots());
  }, []);

  const handleTimeSelect = (value: string) => {
    if (value === "asap") {
      // For ASAP, return current time + 15 minutes as ISO string
      const asapTime = addMinutes(new Date(), 15);
      onTimeSelect(asapTime.toISOString());
    } else if (value === "") {
      onTimeSelect("");
    } else {
      // Find the corresponding slot and return its ISO string
      const slot = timeSlots.find((slot) => format(slot, "HH:mm") === value);
      if (slot) {
        onTimeSelect(slot.toISOString());
      }
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor="time">
          When should your order be ready for pick-up?
        </Label>
        <div className="relative">
          {isValid !== undefined && isValid ? (
            <CircleCheckIcon
              className="absolute top-1/2 -translate-y-1/2 right-2 text-green-600"
              size={22}
            />
          ) : isValid !== undefined && !isValid ? (
            <CircleAlertIcon
              className="absolute top-1/2 -translate-y-1/2 right-2 text-red-500"
              size={22}
            />
          ) : null}

          {timeSlots.length > 0 ? (
            <select
              id="time"
              className={cn(
                "w-full border border-foreground rounded-md px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none",
                isValid === undefined ? "pr-3" : "pr-8"
              )}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition:
                  isValid === undefined
                    ? "right 0.5rem center"
                    : "right 2rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.5em 1.5em",
              }}
              value={(() => {
                if (!selectedTime) return "";
                if (selectedTime === "asap") return "asap";
                if (selectedTime.includes("T")) {
                  // It's an ISO string, convert back to display format
                  const date = new Date(selectedTime);
                  // Check if it's close to ASAP time (within 20 minutes of now)
                  const now = new Date();
                  const timeDiff = date.getTime() - now.getTime();
                  if (timeDiff < 20 * 60 * 1000 && timeDiff > 10 * 60 * 1000) {
                    return "asap";
                  }
                  return format(date, "HH:mm");
                }
                return selectedTime;
              })()}
              onChange={(e) => handleTimeSelect(e.target.value)}
            >
              <option value="" disabled>
                Select a time slot
              </option>
              <option value="asap">As soon as possible</option>
              {timeSlots.map((slot, index) => (
                <option key={index} value={format(slot, "HH:mm")}>
                  {format(slot, "HH:mm")}
                </option>
              ))}
            </select>
          ) : (
            <div className="flex items-center gap-2 w-full border border-yellow-500 bg-yellow-50 rounded-md px-3 py-2 text-sm text-muted-foreground">
              <CircleAlertIcon />
              <p className="text-sm text-muted-foreground">
                No available time slots - please try again tomorrow, or call if
                you need assistance.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
